import * as React from "react";
import { Eye, EyeOff } from "lucide-react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

/**
 * Password field with a show/hide eye toggle on the right. Drop-in
 * replacement for `<Input type="password" />`. Accepts every native
 * input prop; the toggle button is decorative and not focus-trapped
 * (Tab still moves to the next form field).
 *
 * Usage: <PasswordInput data-testid="login-password" value={...} onChange={...} required />
 */
const PasswordInput = React.forwardRef(({ className, "data-testid": testId, ...props }, ref) => {
  const [visible, setVisible] = React.useState(false);
  const toggleTestId = testId ? `${testId}-toggle` : "password-toggle";
  return (
    <div className="relative">
      <Input
        {...props}
        ref={ref}
        type={visible ? "text" : "password"}
        // iOS Safari applies auto-capitalize + smart-punctuation to
        // `type="text"` inputs even when the user briefly toggles the
        // "show password" eye. Straight apostrophes get silently
        // swapped to curly quotes, breaking bcrypt verification and
        // producing "Invalid credentials" for iPhone users only.
        // Explicit attributes suppress all four transformations
        // regardless of the current `type` value.
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        data-testid={testId}
        className={cn("pr-10", className)}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        data-testid={toggleTestId}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        tabIndex={-1}
        className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-stone-500 hover:text-stone-700 transition"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
});
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
